import React, { useState, useEffect, useRef } from 'react'
import styled, { css } from 'styled-components'
import { Link, useLocation } from 'react-router-dom'
import { LocalizedLink, useTranslation, useLang, localizePath, stripLang } from '../../lib/i18n'
import { LANGUAGES } from '../../lib/locale'
import FlagIcon from '../flags'
import LogoImage from '../../img/logo.webp'
import { small } from '../../lib/media_query'
import PlusModal from '../plus_modal'
import PlusPill from '../plus_pill'
import { FaBars, FaTimes, FaChevronDown } from 'react-icons/fa'

const HEIGHT = 70

const HeaderBar = styled.div`
    height: ${HEIGHT}px;
    background: white;
    position: sticky;
    top: 0px;
    z-index: 9;
    border-bottom: 1px solid ${(props) => props.theme.palette.greyScale.lighterGrey};
`

const GridContainer = styled.div`
    height: 100%;

    > .grid-x {
        height: 100%;
    }

    a {
        margin-bottom: 0px;
    }
`

const LogoWrapper = styled.div`
    display: flex;
    align-items: center;
`

const Logo = styled.div`
    background-image: url('${LogoImage}');
    height: 40px;
    width: 40px;
    background-size: 40px 40px;
    margin-right: 10px;
    background-repeat: no-repeat;
`

const LogoText = styled.span`
    font-size: 1.4rem;
    font-weight: 400;
    color: ${(props) => props.theme.palette.greyScale.darkGrey};
`

const navLinkStyles = css`
    font-size: 14px;
    font-weight: 500;
    color: ${(props) => props.theme.palette.greyScale.grey};
    text-decoration: none;
    margin-left: 8px;
    padding: 8px 16px;
    border-radius: 20px;
    transition: background-color 0.2s ease, color 0.2s ease;

    ${(props) => props.isActive && `
        background-color: #f5f5f5;
        color: ${props.theme.palette.greyScale.darkGrey};
    `}

    &:hover {
        background-color: #f5f5f5;
        color: ${(props) => props.theme.palette.greyScale.grey};
    }
`

const NavLink = styled(LocalizedLink)`${navLinkStyles}`

const NavLinks = styled.div`
    display: flex;
    align-items: center;

    ${small(`
        display: none;
    `)}
`

const PlusNavButton = styled.button`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 14px;
    font-weight: 500;
    color: ${(props) => props.theme.palette.greyScale.grey};
    background: none;
    border: none;
    cursor: pointer;
    padding: 0;
    margin-left: 8px;

    &:hover {
        opacity: 0.8;
    }
`

const MobileControls = styled.div`
    display: none;
    align-items: center;

    ${small(`
        display: flex;
    `)}
`

const BurgerButton = styled.button`
    display: none;
    background: none;
    border: none;
    cursor: pointer;
    padding: 8px;
    color: ${(props) => props.theme.palette.greyScale.grey};
    font-size: 20px;

    ${small(`
        display: flex;
        align-items: center;
    `)}
`

const MobileLangButton = styled.button`
    && {
        border-radius: 20px;
    }

    display: none;
    align-items: center;
    gap: 6px;
    padding: 6px 10px;
    margin-right: 4px;
    font-size: 13px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: ${(props) => props.theme.palette.greyScale.grey};
    background: none;
    border: 1px solid ${(props) => props.theme.palette.greyScale.lighterGrey};
    cursor: pointer;
    line-height: 1;

    svg:last-child {
        font-size: 9px;
        opacity: 0.6;
    }

    ${small(`
        display: inline-flex;
    `)}
`

const MobileMenu = styled.div`
    display: none;

    ${small(`
        display: block;
    `)}

    position: absolute;
    top: ${HEIGHT}px;
    left: 0;
    right: 0;
    background: white;
    border-bottom: 1px solid ${(props) => props.theme.palette.greyScale.lighterGrey};
    padding: 16px 0;
    z-index: 8;
`

const MobileLangPanel = styled(MobileMenu)`
    padding: 16px 0 20px;
`

const mobileNavLinkStyles = css`
    display: block;
    padding: 12px 24px;
    font-size: 16px;
    font-weight: 500;
    color: ${(props) => props.theme.palette.greyScale.grey};
    text-decoration: none;
    margin: 0 16px;
    border-radius: 8px;
    transition: background-color 0.2s ease, color 0.2s ease;

    ${(props) => props.isActive && `
        background-color: #f5f5f5;
        color: ${props.theme.palette.greyScale.darkGrey};
    `}

    &:hover {
        background-color: #f5f5f5;
        color: ${(props) => props.theme.palette.greyScale.grey};
    }
`

const MobileNavLink = styled(LocalizedLink)`${mobileNavLinkStyles}`

const MobileLangSection = styled.div`
    margin: 0 16px;
    padding: 0 14px;
`

const MobileLangLabel = styled.div`
    font-size: 12px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: ${(props) => props.theme.palette.greyScale.lightGrey};
    margin-bottom: 8px;
`

const MobileLangGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 4px;
`

const MobileLangOption = styled(Link)`
    display: flex;
    align-items: center;
    gap: 10px;
    min-height: 44px;
    padding: 8px 10px;
    font-size: 15px;
    border-radius: 8px;
    text-decoration: none;
    font-weight: ${(props) => (props.$active ? '600' : '500')};
    color: ${(props) => props.$active
        ? props.theme.palette.greyScale.darkGrey
        : props.theme.palette.greyScale.grey};
    background: ${(props) => (props.$active ? '#f5f5f5' : 'transparent')};

    &:hover {
        background: #f5f5f5;
    }
`

// Every language is listed at once: mobile users need to spot their own
// language, so a collapsed "current language" row would hide the point.
const MobileLanguagePicker = ({ label }) => {
    const { pathname } = useLocation()
    const lang = useLang()
    const basePath = stripLang(pathname)

    return (
        <MobileLangSection>
            <MobileLangLabel>{label}</MobileLangLabel>
            <MobileLangGrid>
                {LANGUAGES.map(({ code, name }) => (
                    <MobileLangOption
                        key={code}
                        to={localizePath(basePath, code)}
                        $active={code === lang}
                        hrefLang={code}
                        lang={code}
                    >
                        <FlagIcon code={code} />
                        {name}
                    </MobileLangOption>
                ))}
            </MobileLangGrid>
        </MobileLangSection>
    )
}

const MobilePlusButton = styled.button`
    display: flex;
    align-items: center;
    gap: 6px;
    width: 100%;
    padding: 12px 24px;
    font-size: 16px;
    font-weight: 500;
    color: ${(props) => props.theme.palette.greyScale.grey};
    background: none;
    border: none;
    cursor: pointer;
    text-align: left;

    &:hover {
        opacity: 0.8;
    }
`

const LangSwitcher = styled.div`
    position: relative;
    margin-left: 12px;
`

const LangButton = styled.button`
    && {
        border-radius: 20px;
    }

    display: flex;
    align-items: center;
    gap: 6px;
    padding: 6px 10px;
    font-size: 14px;
    font-weight: 500;
    color: ${(props) => props.theme.palette.greyScale.grey};
    background: none;
    border: 1px solid ${(props) => props.theme.palette.greyScale.lighterGrey};
    cursor: pointer;
    line-height: 1;

    svg:last-child {
        font-size: 10px;
        opacity: 0.6;
    }

    &:hover {
        background-color: #f5f5f5;
    }
`

const LangMenu = styled.ul`
    position: absolute;
    top: calc(100% + 6px);
    right: 0;
    margin: 0;
    padding: 6px;
    list-style: none;
    background: white;
    border: 1px solid ${(props) => props.theme.palette.greyScale.lighterGrey};
    border-radius: 10px;
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.12);
    min-width: 170px;
    z-index: 10;

`

const LangOption = styled(Link)`
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 8px 10px;
    font-size: 14px;
    font-weight: ${(props) => (props.$active ? '600' : '500')};
    border-radius: 8px;
    text-decoration: none;
    white-space: nowrap;
    color: ${(props) => props.$active
        ? props.theme.palette.greyScale.darkGrey
        : props.theme.palette.greyScale.grey};
    background: ${(props) => (props.$active ? '#f5f5f5' : 'transparent')};

    &:hover {
        background: #f5f5f5;
        color: ${(props) => props.theme.palette.greyScale.darkGrey};
    }
`

const LanguageLinks = ({ label }) => {
    const { pathname } = useLocation()
    const lang = useLang()
    const basePath = stripLang(pathname)
    const [open, setOpen] = useState(false)
    const wrapperRef = useRef(null)
    const current = LANGUAGES.find((l) => l.code === lang) || LANGUAGES[0]

    useEffect(() => {
        if (!open) return

        const handleClick = (e) => {
            if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
                setOpen(false)
            }
        }

        document.addEventListener('mousedown', handleClick)
        return () => document.removeEventListener('mousedown', handleClick)
    }, [open])

    return (
        <LangSwitcher ref={wrapperRef}>
            <LangButton
                type="button"
                aria-label={`${label}: ${current.name}`}
                aria-expanded={open}
                aria-haspopup="true"
                onClick={() => setOpen(!open)}
            >
                <FlagIcon code={current.code} />
                {current.name}
                <FaChevronDown />
            </LangButton>
            {open && (
                <LangMenu>
                    {LANGUAGES.map(({ code, name }) => (
                        <li key={code}>
                            <LangOption
                                to={localizePath(basePath, code)}
                                $active={code === lang}
                                hrefLang={code}
                                lang={code}
                                onClick={() => setOpen(false)}
                            >
                                <FlagIcon code={code} />
                                {name}
                            </LangOption>
                        </li>
                    ))}
                </LangMenu>
            )}
        </LangSwitcher>
    )
}

export const Header = () => {
    const [plusOpen, setPlusOpen] = useState(false)
    const [autoCheckout, setAutoCheckout] = useState(null)
    const [menuOpen, setMenuOpen] = useState(false)
    const [langOpen, setLangOpen] = useState(false)
    const menuRef = useRef(null)
    const burgerRef = useRef(null)
    const langRef = useRef(null)
    const langButtonRef = useRef(null)
    const location = useLocation()
    const { t } = useTranslation(undefined, { keyPrefix: 'nav' })
    const lang = useLang()

    const currentLang = LANGUAGES.find((l) => l.code === lang) || LANGUAGES[0]

    const isLinkActive = (path) => location.pathname === localizePath(path, lang)

    useEffect(() => {
        setMenuOpen(false)
        setLangOpen(false)
    }, [location.pathname])

    useEffect(() => {
        const params = new URLSearchParams(window.location.search)
        const plan = params.get('checkout')
        if (plan === 'monthly' || plan === 'yearly') {
            setAutoCheckout(plan)
            setPlusOpen(true)
        }
    }, [])

    useEffect(() => {
        if (!menuOpen) return

        const handleClick = (e) => {
            if (
                menuRef.current && !menuRef.current.contains(e.target) &&
                burgerRef.current && !burgerRef.current.contains(e.target)
            ) {
                setMenuOpen(false)
            }
        }

        document.addEventListener('mousedown', handleClick)
        return () => document.removeEventListener('mousedown', handleClick)
    }, [menuOpen])

    useEffect(() => {
        if (!langOpen) return

        const handleClick = (e) => {
            if (
                langRef.current && !langRef.current.contains(e.target) &&
                langButtonRef.current && !langButtonRef.current.contains(e.target)
            ) {
                setLangOpen(false)
            }
        }

        document.addEventListener('mousedown', handleClick)
        return () => document.removeEventListener('mousedown', handleClick)
    }, [langOpen])

    return (
        <HeaderBar>
            <GridContainer className="grid-container">
                <div className="grid-x align-middle align-justify">
                    <div className="cell shrink">
                        <LocalizedLink
                            to="/"
                            onClick={(e) => {
                                if (typeof window !== 'undefined' && window.pageYOffset > 50) {
                                    e.preventDefault()
                                    window.scrollTo({ top: 0, behavior: 'smooth' })
                                }
                            }}
                        >
                            <LogoWrapper>
                                <Logo />
                                <LogoText>Keybreak</LogoText>
                            </LogoWrapper>
                        </LocalizedLink>
                    </div>
                    <div className="cell shrink">
                        <NavLinks>
                            <NavLink to="/break-reminder-app" isActive={isLinkActive('/break-reminder-app')}>{t('breakReminderApp')}</NavLink>
                            <NavLink to="/pricing" isActive={isLinkActive('/pricing')}>{t('pricing')}</NavLink>
                            <NavLink to="/about" isActive={isLinkActive('/about')}>{t('about')}</NavLink>
                            <NavLink to="/blog" isActive={isLinkActive('/blog')}>{t('blog')}</NavLink>
                            <LanguageLinks label={t('language')} />
                        </NavLinks>
                        <MobileControls>
                            <MobileLangButton
                                ref={langButtonRef}
                                type="button"
                                aria-label={`${t('language')}: ${currentLang.name}`}
                                aria-expanded={langOpen}
                                aria-haspopup="true"
                                onClick={() => { setLangOpen(!langOpen); setMenuOpen(false) }}
                            >
                                <FlagIcon code={currentLang.code} />
                                {currentLang.code}
                                <FaChevronDown />
                            </MobileLangButton>
                            <BurgerButton
                                ref={burgerRef}
                                aria-label={menuOpen ? t('closeMenu') : t('openMenu')}
                                onClick={() => { setMenuOpen(!menuOpen); setLangOpen(false) }}
                            >
                                {menuOpen ? <FaTimes /> : <FaBars />}
                            </BurgerButton>
                        </MobileControls>
                    </div>
                </div>
            </GridContainer>
            {menuOpen && (
                <MobileMenu ref={menuRef}>
                    <MobileNavLink to="/break-reminder-app" isActive={isLinkActive('/break-reminder-app')}>{t('breakReminderApp')}</MobileNavLink>
                    <MobileNavLink to="/pricing" isActive={isLinkActive('/pricing')}>{t('pricing')}</MobileNavLink>
                    <MobileNavLink to="/about" isActive={isLinkActive('/about')}>{t('about')}</MobileNavLink>
                    <MobileNavLink to="/blog" isActive={isLinkActive('/blog')}>{t('blog')}</MobileNavLink>
                </MobileMenu>
            )}
            {langOpen && (
                <MobileLangPanel ref={langRef}>
                    <MobileLanguagePicker label={t('language')} />
                </MobileLangPanel>
            )}
            <PlusModal open={plusOpen} onClose={() => { setPlusOpen(false); setAutoCheckout(null) }} autoCheckout={autoCheckout} />
        </HeaderBar>
    )
}

export default Header
